"use client"

import { Link } from "react-router-dom"
import {
  AlertTriangleIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronRightIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
} from "lucide-react"
import type { BranchFinanceBoardAccount, BranchFinanceBoardAttention } from "@ibada/shared-types/overview"
import { BranchAccountReconciliationState } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { ControlVerdict, controlVerdict } from "@/features/finance/overview/branch-f1/lib/branch-f1-format"
import type { TransactionRow } from "@/features/finance/overview/widgets/recent-transactions-widget"
import type { TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { formatCompactNumber } from "@/features/finance/overview/lib/overview-format"
import { useLocale, useTranslations } from "@/i18n"
import { dayShortLabel } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

/** The tone of a count badge: amber for evidence, blue for reconciliation, green for transfers, muted at zero. */
enum AttentionTone {
  Evidence = "evidence",
  Reconcile = "reconcile",
  Transfer = "transfer",
  Ledger = "ledger",
  Clear = "clear",
}

const BADGE_CLASS: Record<AttentionTone, string> = {
  [AttentionTone.Evidence]: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  [AttentionTone.Reconcile]: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  [AttentionTone.Transfer]: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  [AttentionTone.Ledger]: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  [AttentionTone.Clear]: "bg-muted text-muted-foreground",
}

function AttentionRow({
  count,
  title,
  detail,
  tone,
  href,
}: {
  count: string
  title: string
  detail: string
  tone: AttentionTone
  href: string | null
}) {
  const body = (
    <>
      <span className={cn("inline-flex h-[26px] min-w-[34px] shrink-0 items-center justify-center rounded-lg px-1.5 text-[13px] font-extrabold tabular-nums", BADGE_CLASS[tone])}>
        {count}
      </span>
      <span className='flex min-w-0 flex-col'>
        <span className='truncate text-[13px] font-bold text-foreground'>{title}</span>
        <span className='truncate text-[11.5px] text-muted-foreground'>{detail}</span>
      </span>
      {href ? <ChevronRightIcon aria-hidden className='ml-auto size-4 shrink-0 text-muted-foreground' /> : null}
    </>
  )
  return (
    <li data-testid='branch-attention-row'>
      {href ? (
        <Link to={href} className='flex items-center gap-2.5 py-1.5 hover:text-primary'>
          {body}
        </Link>
      ) : (
        <div className='flex items-center gap-2.5 py-1.5'>{body}</div>
      )}
    </li>
  )
}

/**
 * To do (F1): what blocks a clean close, each with its count, one line of detail and where to resolve it. Links
 * only navigate; nothing here changes data.
 */
export function BranchAttentionTile({
  attention,
  accounts,
  balanced,
  currency,
  during,
  hrefs,
}: {
  attention: BranchFinanceBoardAttention
  accounts: readonly BranchFinanceBoardAccount[]
  /** Null while the trial balance loads or when it failed: the ledger row is then left out. */
  balanced: boolean | null
  currency: string
  during: string
  hrefs: { evidence: string | null; reconcile: string | null; transfers: string | null; ledger: string | null }
}) {
  const t = useTranslations("finance.overview")
  const toReconcile = accounts.filter((account) => account.reconciliation !== BranchAccountReconciliationState.Reconciled)
  const first = attention.firstTransferToAccept
  return (
    <HomeTile title={t("widgets.branchAttention.title")} subtitle={t("widgets.branchAttention.subtitle")} testId='branch-tile-attention'>
      <ul className='flex flex-col divide-y divide-border/60 border-t border-border/60'>
        <AttentionRow
          count={formatCompactNumber(attention.missingEvidence)}
          title={t("branch.attention.evidence")}
          detail={
            attention.missingEvidence === 0
              ? t("branch.attention.evidenceNone")
              : t("branch.attention.evidenceDuring", { count: attention.missingEvidenceInPeriod, during })
          }
          tone={attention.missingEvidence > 0 ? AttentionTone.Evidence : AttentionTone.Clear}
          href={attention.missingEvidence > 0 ? hrefs.evidence : null}
        />
        <AttentionRow
          count={String(attention.accountsToReconcile)}
          title={t("branch.attention.reconcile")}
          detail={toReconcile.length > 0 ? toReconcile.map((account) => account.name).join(", ") : t("branch.attention.reconcileNone")}
          tone={attention.accountsToReconcile > 0 ? AttentionTone.Reconcile : AttentionTone.Clear}
          href={attention.accountsToReconcile > 0 ? hrefs.reconcile : null}
        />
        <AttentionRow
          count={String(attention.transfersToAccept)}
          title={t("branch.attention.transfers")}
          detail={
            first
              ? t("branch.attention.transferFrom", {
                  source: first.sourceIsParish ? t("widgets.branchPerformance.parish") : first.sourceBranchName,
                  amount: formatCurrency(first.amount, currency),
                })
              : t("branch.attention.transfersNone")
          }
          tone={attention.transfersToAccept > 0 ? AttentionTone.Transfer : AttentionTone.Clear}
          href={attention.transfersToAccept > 0 ? hrefs.transfers : null}
        />
        {balanced === null ? null : (
          <AttentionRow
            count={balanced ? "0" : "!"}
            title={t("branch.attention.ledger")}
            detail={balanced ? t("branch.attention.ledgerBalanced") : t("branch.attention.ledgerUnbalanced")}
            tone={balanced ? AttentionTone.Clear : AttentionTone.Ledger}
            href={balanced ? null : hrefs.ledger}
          />
        )}
      </ul>
    </HomeTile>
  )
}

const PILL_CLASS: Record<BranchAccountReconciliationState, string> = {
  [BranchAccountReconciliationState.Reconciled]: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  [BranchAccountReconciliationState.Never]: "bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
  [BranchAccountReconciliationState.Overdue]: "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300",
}

/** How many accounts the reconciliation tile lists. */
const RECONCILIATION_ROWS = 4

/** Reconciliation (F1): per account, when it was last reconciled and whether that is current, late or never. */
export function BranchReconciliationTile({
  accounts,
  action,
}: {
  accounts: readonly BranchFinanceBoardAccount[]
  /** "Start a reconciliation" for a reader who may, else "See reconciliations", else nothing. */
  action: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  // Late and never first: they are what the tile is for.
  const order = [BranchAccountReconciliationState.Overdue, BranchAccountReconciliationState.Never, BranchAccountReconciliationState.Reconciled]
  const rows = [...accounts].sort((a, b) => order.indexOf(a.reconciliation) - order.indexOf(b.reconciliation)).slice(0, RECONCILIATION_ROWS)
  return (
    <HomeTile title={t("widgets.branchReconciliation.title")} subtitle={t("widgets.branchReconciliation.subtitle")} testId='branch-tile-reconciliation'>
      {rows.length === 0 ? (
        <HomeTileEmpty message={t("branch.reconciliation.empty")} />
      ) : (
        <ul className='flex flex-col divide-y divide-border/60 border-t border-border/60'>
          {rows.map((account) => (
            <li key={account.accountId} data-testid='branch-reconciliation-row' className='flex items-center gap-2.5 py-1.5'>
              <span className='flex min-w-0 flex-col'>
                <span className='truncate text-[13px] font-bold text-foreground'>{account.name}</span>
                <span className='truncate text-[11.5px] text-muted-foreground'>
                  {account.daysSinceReconciled === null
                    ? t("branch.reconciliation.never")
                    : account.daysSinceReconciled === 0
                      ? t("branch.reconciliation.today")
                      : t(account.daysSinceReconciled === 1 ? "branch.reconciliation.daysAgoOne" : "branch.reconciliation.daysAgoOther", {
                        count: account.daysSinceReconciled,
                      })}
                  {account.hasOpenSession ? `, ${t("branch.reconciliation.inProgress")}` : ""}
                </span>
              </span>
              <span className={cn("ml-auto shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold", PILL_CLASS[account.reconciliation])}>
                {t(`branch.reconciliation.state.${account.reconciliation}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {action ? (
        <Link to={action.href} className='mt-auto inline-flex items-center gap-0.5 text-[13px] font-bold text-primary hover:underline'>
          {action.label}
          <ChevronRightIcon aria-hidden className='size-3.5' />
        </Link>
      ) : null}
    </HomeTile>
  )
}

const VERDICT_CLASS: Record<ControlVerdict, string> = {
  [ControlVerdict.Ready]: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  [ControlVerdict.AlmostReady]: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  [ControlVerdict.NotReady]: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
}

function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li data-testid='branch-control-check' data-ok={ok} className='flex items-center gap-2 text-[12.5px]'>
      {ok ? (
        <CheckIcon aria-hidden className='size-4 shrink-0 text-emerald-600 dark:text-emerald-400' />
      ) : (
        <AlertTriangleIcon aria-hidden className='size-4 shrink-0 text-amber-600 dark:text-amber-400' />
      )}
      <span className='text-foreground'>{label}</span>
    </li>
  )
}

/**
 * Control (F1): is the period ready to close? A verdict from the checks the system can prove (a balanced ledger,
 * no cancelled or voided entries left to review, no entry of the period without its supporting document).
 */
export function BranchControlTile({
  balanced,
  reviewable,
  missingInPeriod,
  toReconcile,
  during,
  link,
}: {
  balanced: boolean
  reviewable: number
  missingInPeriod: number
  /** Accounts never reconciled or overdue. */
  toReconcile: number
  during: string
  link: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const { verdict, points } = controlVerdict({ balanced, reviewable, missingInPeriod, toReconcile })
  const Icon = verdict === ControlVerdict.Ready ? ShieldCheckIcon : ShieldAlertIcon
  return (
    <HomeTile title={t("widgets.branchControl.title")} subtitle={t("branch.control.subtitle", { during })} link={link} testId='branch-tile-control'>
      <div data-testid='branch-control-verdict' data-verdict={verdict} className={cn("flex items-center gap-2.5 rounded-xl px-3 py-2.5", VERDICT_CLASS[verdict])}>
        <Icon aria-hidden className='size-5 shrink-0' />
        <span className='flex flex-col'>
          <b className='text-sm text-foreground'>{t(`branch.control.verdict.${verdict}`)}</b>
          <span className='text-xs text-muted-foreground'>
            {points === 0 ? t("branch.control.nothingLeft") : t(points === 1 ? "branch.control.pointsOne" : "branch.control.pointsOther", { count: points })}
          </span>
        </span>
      </div>
      <ul className='flex flex-col gap-1.5'>
        <CheckRow ok={balanced} label={t(balanced ? "branch.control.balanced" : "branch.control.unbalanced")} />
        <CheckRow
          ok={reviewable === 0}
          label={reviewable === 0 ? t("branch.control.noReversal") : t(reviewable === 1 ? "branch.control.reversalsOne" : "branch.control.reversalsOther", { count: reviewable })}
        />
        <CheckRow
          ok={missingInPeriod === 0}
          label={
            missingInPeriod === 0
              ? t("branch.control.evidenceComplete", { during })
              : t(missingInPeriod === 1 ? "branch.control.evidenceMissingOne" : "branch.control.evidenceMissingOther", { count: missingInPeriod, during })
          }
        />
        <CheckRow
          ok={toReconcile === 0}
          label={
            toReconcile === 0
              ? t("branch.control.reconciled")
              : t(toReconcile === 1 ? "branch.control.toReconcileOne" : "branch.control.toReconcileOther", { count: toReconcile })
          }
        />
      </ul>
    </HomeTile>
  )
}

/** How many operations the recent tile lists. */
export const RECENT_OPERATION_ROWS = 4

/** Latest operations (F1): the last few entries of the period with their signed amounts. */
export function BranchRecentTile({
  rows,
  currency,
  subtitle,
  fallback,
  link,
}: {
  rows: readonly TransactionRow[]
  currency: string
  subtitle: string
  fallback: TileFallback | null
  link: HomeTileLink | null
}) {
  const t = useTranslations("finance.overview")
  const locale = useLocale()
  return (
    <HomeTile title={t("widgets.branchRecent.title")} subtitle={fallback?.label ?? subtitle} link={link} testId='branch-tile-recent'>
      <ul className='flex flex-col divide-y divide-border/60 border-t border-border/60'>
        {rows.slice(0, RECENT_OPERATION_ROWS).map((row) => (
          <li key={row.id} data-testid='branch-recent-row' className='flex items-center gap-2.5 py-1.5'>
            <span
              className={cn(
                "inline-flex size-7 shrink-0 items-center justify-center rounded-lg",
                row.isIncome ? "bg-teal-500/10 text-teal-700 dark:text-teal-300" : "bg-muted text-muted-foreground",
              )}
            >
              {row.isIncome ? <ArrowDownLeftIcon aria-hidden className='size-3.5' /> : <ArrowUpRightIcon aria-hidden className='size-3.5' />}
            </span>
            <span className='flex min-w-0 flex-col'>
              <span className='truncate text-[13px] font-bold text-foreground'>{row.label}</span>
              <span className='truncate text-[11.5px] text-muted-foreground'>
                {row.occurredAt ? dayShortLabel(row.occurredAt, locale) : row.date}
              </span>
            </span>
            <span className={cn("ml-auto shrink-0 text-[13px] font-bold whitespace-nowrap tabular-nums", row.isIncome ? "text-teal-700 dark:text-teal-300" : "text-foreground")}>
              {`${row.isIncome ? "+" : "−"}${formatCurrency(row.amount, currency)}`}
            </span>
          </li>
        ))}
      </ul>
    </HomeTile>
  )
}
